import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';

import {
  DraftPolicySection,
  TreeItems,
  DraftPolicy,
  FlattenedItem,
  DraftManual,
  DraftPolicySubSection,
} from 'views/App/Portal/Policy/_types/manuals/draft';

import { SortableTree } from './sortableTree';

interface ModifyPolicyStructureProps {
  onClose: () => void;
  sections: any; // Updated type
  policyData: DraftPolicy;
  manual: DraftManual;
  updatePolicyData: (updatedData: Partial<DraftPolicy>) => void;
  onChange: (updatedSection: DraftPolicySection | DraftPolicySubSection) => void;
}

const ModifyPolicyStructure: React.FC<ModifyPolicyStructureProps> = ({
  onClose,
  sections,
  updatePolicyData,
  policyData,
  manual,
  onChange,
}) => {
  const transformSectionsToTreeItems = (sections: DraftPolicySection[]): TreeItems => {
    return sections.map((section) => ({
      attachments: section.attachments,
      id: section.id,
      title: section.title,
      content: section.content,
      contentEditable: section.contentEditable,
      state: section.state,
      agencyModified: section.agencyModified,
      editLevelTag: section.editLevelTag,
      jurisdictionSectionId: section.jurisdictionSectionId,
      parentId: section.parentId ?? null,
      depth: 0,
      sectionNumber: '',
      isSubSection: false,
      collapsed: false,
      subSections: [],
      isDeleted: section.isDeleted,
      children: (section.subSections ?? []).map((sub) => ({
        attachments: sub.attachments,
        id: sub.id,
        title: sub.title,
        content: sub.content,
        contentEditable: sub.contentEditable,
        state: sub.state,
        agencyModified: sub.agencyModified,
        editLevelTag: sub.editLevelTag,
        jurisdictionSubSectionId: sub.jurisdictionSubSectionId,
        parentId: section.id,
        depth: 1,
        sectionNumber: '',
        isSubSection: true,
        subSections: [],
        collapsed: false,
        children: [],
        isDeleted: sub.isDeleted,
      })),
    }));
  };

  const transformTreeItemsToSections = (tree: TreeItems): DraftPolicySection[] => {
    return tree.map((node) => ({
      attachments: node.attachments,
      id: node.id,
      title: node.title,
      content: node.content,
      contentEditable: node.contentEditable,
      state: node.state,
      agencyModified: node.agencyModified,
      editLevelTag: node.editLevelTag,
      parentId: node.parentId ?? '',
      depth: node.depth,
      sectionNumber: node.sectionNumber,
      isSubSection: node.isSubSection,
      collapsed: node.collapsed,
      isDeleted: node.isDeleted,
      subSections: node.children.map((child) => ({
        attachments: child.attachments,
        id: child.id,
        title: child.title,
        content: child.content,
        contentEditable: child.contentEditable,
        state: child.state,
        agencyModified: child.agencyModified,
        editLevelTag: child.editLevelTag,
        parentId: child.parentId ?? '',
        depth: child.depth,
        sectionNumber: child.sectionNumber,
        isSubSection: child.isSubSection,
        collapsed: child.collapsed,
        subSections: [],
        isDeleted: child.isDeleted,
      })),
    }));
  };

  const [treeItems, setTreeItems] = useState<TreeItems>(transformSectionsToTreeItems(sections));

  useEffect(() => {
    setTreeItems(transformSectionsToTreeItems(sections));
  }, [sections]);

  const handleTreeChange = (updatedTree: TreeItems) => {
    const updatedSections = transformTreeItemsToSections(updatedTree);
    updatePolicyData({ sections: updatedSections });
    setTreeItems(transformSectionsToTreeItems(updatedSections));
  };

  const handleSectionChange = useCallback(
    (updatedSection: FlattenedItem) => {
      const section: DraftPolicySection | DraftPolicySubSection = updatedSection.isSubSection
        ? ({
            ...updatedSection,
          } as DraftPolicySubSection)
        : ({
            ...updatedSection,
          } as DraftPolicySection);

      onChange(section);
    },
    [onChange],
  );

  return (
    <Box>
      <Box>
        <SortableTree
          collapsible
          indicator
          removable
          items={treeItems}
          onChange={handleTreeChange}
          onSectionChange={handleSectionChange}
          manual={manual}
          policyData={policyData}
        />
      </Box>
    </Box>
  );
};

export default ModifyPolicyStructure;
